/* =============================================================================
 * Enum values (exactly as the backend expects them) + Marathi labels.
 * Values come from the OpenAPI specs; never change a `value`, only labels.
 * ========================================================================== */

import type {
  CandidateType,
  CollegeLevel,
  Gender,
  Language,
  QualificationLevel,
  Region,
  State,
} from '../types.ts';

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

/** 36 districts — `value` casing/spacing must match the backend exactly. */
export const DISTRICTS: (Option & { region: Region })[] = [
  { value: 'Ahmednagar', label: 'अहिल्यानगर (अहमदनगर)', region: 'nashik' },
  { value: 'Akola', label: 'अकोला', region: 'amravati' },
  { value: 'Amravati', label: 'अमरावती', region: 'amravati' },
  { value: 'Beed', label: 'बीड', region: 'chatrapati_sambhajinagar' },
  { value: 'Bhandara', label: 'भंडारा', region: 'nagpur' },
  { value: 'Buldhana', label: 'बुलढाणा', region: 'amravati' },
  { value: 'Chandrapur', label: 'चंद्रपूर', region: 'nagpur' },
  { value: 'Chhatrapati Sambhajinagar', label: 'छत्रपती संभाजीनगर', region: 'chatrapati_sambhajinagar' },
  { value: 'Dharashiv', label: 'धाराशिव', region: 'chatrapati_sambhajinagar' },
  { value: 'Dhule', label: 'धुळे', region: 'nashik' },
  { value: 'Gadchiroli', label: 'गडचिरोली', region: 'nagpur' },
  { value: 'Gondia', label: 'गोंदिया', region: 'nagpur' },
  { value: 'Hingoli', label: 'हिंगोली', region: 'chatrapati_sambhajinagar' },
  { value: 'Jalgaon', label: 'जळगाव', region: 'nashik' },
  { value: 'Jalna', label: 'जालना', region: 'chatrapati_sambhajinagar' },
  { value: 'Kolhapur', label: 'कोल्हापूर', region: 'pune' },
  { value: 'Latur', label: 'लातूर', region: 'chatrapati_sambhajinagar' },
  { value: 'Mumbai City', label: 'मुंबई शहर', region: 'mumbai' },
  { value: 'Mumbai Suburban', label: 'मुंबई उपनगर', region: 'mumbai' },
  { value: 'Nagpur', label: 'नागपूर', region: 'nagpur' },
  { value: 'Nanded', label: 'नांदेड', region: 'chatrapati_sambhajinagar' },
  { value: 'Nandurbar', label: 'नंदुरबार', region: 'nashik' },
  { value: 'Nashik', label: 'नाशिक', region: 'nashik' },
  { value: 'Palghar', label: 'पालघर', region: 'mumbai' },
  { value: 'Parbhani', label: 'परभणी', region: 'chatrapati_sambhajinagar' },
  { value: 'Pune', label: 'पुणे', region: 'pune' },
  { value: 'Raigad', label: 'रायगड', region: 'mumbai' },
  { value: 'Ratnagiri', label: 'रत्नागिरी', region: 'mumbai' },
  { value: 'Sangli', label: 'सांगली', region: 'pune' },
  { value: 'Satara', label: 'सातारा', region: 'pune' },
  { value: 'Sindhudurg', label: 'सिंधुदुर्ग', region: 'mumbai' },
  { value: 'Solapur', label: 'सोलापूर', region: 'pune' },
  { value: 'Thane', label: 'ठाणे', region: 'mumbai' },
  { value: 'Wardha', label: 'वर्धा', region: 'nagpur' },
  { value: 'Washim', label: 'वाशिम', region: 'amravati' },
  { value: 'Yavatmal', label: 'यवतमाळ', region: 'amravati' },
];

/** Marathi alphabetical order for dropdowns. */
export const DISTRICTS_SORTED = [...DISTRICTS].sort((a, b) => a.label.localeCompare(b.label, 'mr'));

export const districtLabel = (v: string) => DISTRICTS.find((d) => d.value === v)?.label ?? v;

export const REGIONS: Option<Region>[] = [
  { value: 'pune', label: 'पुणे विभाग' },
  { value: 'mumbai', label: 'मुंबई विभाग' },
  { value: 'nashik', label: 'नाशिक विभाग' },
  { value: 'chatrapati_sambhajinagar', label: 'छत्रपती संभाजीनगर विभाग' },
  { value: 'amravati', label: 'अमरावती विभाग' },
  { value: 'nagpur', label: 'नागपूर विभाग' },
];
export const regionLabel = (v: string) => REGIONS.find((r) => r.value === v)?.label ?? v;

export const STATES: Option<State>[] = [{ value: 'maharashtra', label: 'महाराष्ट्र' }];
export const stateLabel = (v: string) => STATES.find((s) => s.value === v)?.label ?? v;

export const GENDERS: Option<Gender>[] = [
  { value: 'male', label: 'पुरुष' },
  { value: 'female', label: 'स्त्री' },
];
export const genderLabel = (v: string) => GENDERS.find((g) => g.value === v)?.label ?? v;

export const LANGUAGES: Option<Language>[] = [
  { value: 'marathi', label: 'मराठी' },
  { value: 'english', label: 'इंग्रजी' },
  { value: 'hindi', label: 'हिंदी' },
];
export const languageLabel = (v: string) => LANGUAGES.find((l) => l.value === v)?.label ?? v;

/* -----------------------------------------------------------------------------
 * Candidates see TWO groups: शालेय (school) and खुला (open). The open group is
 * split by one question — college student or working/other — which decides the
 * backend's candidate_type: 'college' or 'open'.
 * -------------------------------------------------------------------------- */

export type Group = 'school' | 'open';

export const GROUPS: (Option<Group> & { desc: string; eligibility: string })[] = [
  {
    value: 'school',
    label: 'शालेय गट',
    desc: 'माध्यमिक आणि उच्च माध्यमिक विद्यार्थी (इयत्ता ७वी ते १२वी)',
    eligibility: '१ जानेवारी २००९ रोजी किंवा त्यानंतर जन्मलेले',
  },
  {
    value: 'open',
    label: 'खुला गट',
    desc: 'महाविद्यालयीन विद्यार्थी आणि नोकरी / व्यवसाय करणारे सर्व शिवभक्त / शिवचरित्र अभ्यासक',
    eligibility: '१ जानेवारी १९८२ ते ३१ डिसेंबर २००८ दरम्यान जन्मलेले (दोन्ही तारखा समाविष्ट)',
  },
];

export const groupOf = (t: CandidateType | ''): Group | '' => (t === 'school' ? 'school' : t ? 'open' : '');
export const groupLabel = (g: string) => GROUPS.find((x) => x.value === g)?.label ?? g;

/** The sub-question inside the open group. */
export const OPEN_SUBTYPES: (Option<'college' | 'open'> & { desc: string })[] = [
  { value: 'college', label: 'महाविद्यालयीन विद्यार्थी', desc: 'पदवी, पदव्युत्तर, डिप्लोमा किंवा पीएच.डी. चे शिक्षण घेत आहे' },
  { value: 'open', label: 'नोकरी / व्यवसाय करणारे', desc: 'नोकरी, व्यवसाय, शेती, गृहिणी, सेवानिवृत्त इ.' },
];

export const CANDIDATE_TYPES: (Option<CandidateType> & { short: string; desc: string })[] = [
  { value: 'school', label: 'शालेय गट', short: 'शालेय', desc: GROUPS[0].desc },
  { value: 'college', label: 'खुला गट — महाविद्यालयीन विद्यार्थी', short: 'खुला · विद्यार्थी', desc: OPEN_SUBTYPES[0].desc },
  { value: 'open', label: 'खुला गट — नोकरी / व्यवसाय', short: 'खुला · व्यावसायिक', desc: OPEN_SUBTYPES[1].desc },
];
export const typeLabel = (v: string) => CANDIDATE_TYPES.find((t) => t.value === v)?.label ?? v;
export const typeShort = (v: string) => CANDIDATE_TYPES.find((t) => t.value === v)?.short ?? v;

export const COLLEGE_LEVELS: Option<CollegeLevel>[] = [
  { value: 'undergraduate', label: 'पदवी (Undergraduate)' },
  { value: 'postgraduate', label: 'पदव्युत्तर (Postgraduate)' },
  { value: 'doctorate', label: 'डॉक्टरेट (Ph.D.)' },
];
export const collegeLevelLabel = (v: string) => COLLEGE_LEVELS.find((l) => l.value === v)?.label ?? v;

export const QUALIFICATIONS: Option<QualificationLevel>[] = [
  { value: 'below_10th', label: '१०वी पेक्षा कमी' },
  { value: '10th', label: '१०वी' },
  { value: '12th', label: '१२वी' },
  { value: 'iti', label: 'आयटीआय' },
  { value: 'diploma', label: 'डिप्लोमा' },
  { value: 'undergraduate', label: 'पदवी' },
  { value: 'postgraduate', label: 'पदव्युत्तर' },
  { value: 'doctorate', label: 'डॉक्टरेट (Ph.D.)' },
  { value: 'other', label: 'इतर' },
];
export const qualificationLabel = (v: string) => QUALIFICATIONS.find((q) => q.value === v)?.label ?? v;
